import { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, CheckCircle2, Info, X, ShieldCheck, Search } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS_IT = ["", "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

/**
 * Banner che monitora se i dati caricati nel portale corrispondono ai bilanci PDF originali.
 * Mostra un alert ROSSO solo se ci sono mismatch GRAVI (>0,5% sul fatturato o utile).
 * Le piccole differenze classificatorie (commissioni POS ecc.) finiscono in "ok_minor" e non spaventano l'utente.
 * Per i periodi caricati prima dell'introduzione del check si vede un sommesso indicatore "non verificato".
 */
export const DataHealthBanner = ({ clientId, isAdmin }) => {
  const [health, setHealth] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [accepting, setAccepting] = useState({});
  const [diagOpen, setDiagOpen] = useState(false);
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagData, setDiagData] = useState(null);

  const refresh = async () => {
    try {
      const res = await axios.get(`${API}/clients/${clientId}/data-health`);
      setHealth(res.data);
    } catch (e) { /* */ }
  };

  useEffect(() => {
    if (!clientId || !isAdmin) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, isAdmin]);

  const handleAccept = async (periodId) => {
    setAccepting(s => ({ ...s, [periodId]: true }));
    try {
      await axios.post(`${API}/clients/${clientId}/periods/${periodId}/accept-mismatch`);
      toast.success("Differenza marcata come accettata");
      await refresh();
    } catch (e) {
      toast.error("Errore");
    } finally {
      setAccepting(s => ({ ...s, [periodId]: false }));
    }
  };

  const handleDiagnose = async (year, month) => {
    setDiagOpen(true);
    setDiagLoading(true);
    setDiagData(null);
    try {
      const res = await axios.get(`${API}/clients/${clientId}/data-health/diagnose`, {
        params: { year, month }
      });
      setDiagData(res.data);
    } catch (e) {
      toast.error("Errore nell'analisi");
      setDiagOpen(false);
    } finally {
      setDiagLoading(false);
    }
  };

  if (!isAdmin || !health || dismissed) return null;
  // Banner is visible only if there's something to communicate
  const hasMismatches = (health.mismatch_count || 0) > 0;
  const hasMinor = (health.minor_count || 0) > 0;
  const hasUnknown = (health.unknown_count || 0) > 0;
  if (!hasMismatches && !hasMinor && !hasUnknown) return null;

  const sortedIssues = [...(health.issues || [])].sort((a, b) => {
    const order = { mismatch: 0, ok_minor: 1, unknown: 2 };
    if (order[a.status] !== order[b.status]) return order[a.status] - order[b.status];
    if (a.year !== b.year) return a.year - b.year;
    return a.month - b.month;
  });

  // Color/style based on the worst severity present
  const tone = hasMismatches ? 'red' : hasMinor ? 'amber' : 'slate';
  const toneCls = {
    red: { bg: 'bg-red-50 border-red-300', accent: 'text-red-700', accentBg: 'bg-red-100', icon: AlertTriangle },
    amber: { bg: 'bg-amber-50 border-amber-300', accent: 'text-amber-700', accentBg: 'bg-amber-100', icon: AlertTriangle },
    slate: { bg: 'bg-slate-50 border-slate-300', accent: 'text-slate-700', accentBg: 'bg-slate-100', icon: Info },
  };
  const c = toneCls[tone];
  const Icon = c.icon;

  const headline = hasMismatches
    ? `${health.mismatch_count} mese${health.mismatch_count === 1 ? '' : 'i'} non corrisponde${health.mismatch_count === 1 ? '' : 'no'} al bilancio originale`
    : hasMinor
    ? `${health.minor_count} mese${health.minor_count === 1 ? '' : 'i'} con differenze minori (classificazione voci)`
    : `${health.unknown_count} mese${health.unknown_count === 1 ? '' : 'i'} non verificat${health.unknown_count === 1 ? 'o' : 'i'} (caricat${health.unknown_count === 1 ? 'o' : 'i'} prima del controllo automatico)`;

  return (
    <div className={`rounded-lg border-2 ${c.bg} px-4 py-3 mb-4 shadow-sm`} data-testid="data-health-banner">
      <div className="flex items-start gap-3">
        <Icon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${c.accent}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className={`text-sm font-bold ${c.accent}`}>
              Salute dati: {headline}
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={() => setCollapsed(!collapsed)} className="h-7 px-2 text-xs" data-testid="data-health-toggle-btn">
                {collapsed ? "Mostra dettagli" : "Nascondi"}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setDismissed(true)} className="h-7 w-7 p-0" data-testid="data-health-dismiss-btn">
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          {!collapsed && (
            <>
              <div className="text-xs text-slate-600 mt-1">
                {hasMismatches && (
                  <>I mesi marcati in <strong className="text-red-700">rosso</strong> hanno valori che non coincidono col bilancio: <strong>ricarica il PDF originale</strong> dal mese giusto. </>
                )}
                {!hasMismatches && hasMinor && (
                  <>Le differenze sono piccole (sotto lo 0,5%) e dipendono da come alcune voci minori (es. commissioni POS) vengono classificate. Non è un errore. </>
                )}
                {hasUnknown && (
                  <>I mesi "non verificati" sono stati caricati prima dell'introduzione del controllo automatico — per validarli, ricarica il loro PDF originale. </>
                )}
              </div>

              <div className="mt-2 grid sm:grid-cols-2 gap-2">
                {sortedIssues.slice(0, 6).map((issue, i) => {
                  const tagBg = issue.status === 'mismatch' ? 'bg-red-100 text-red-700 border-red-200'
                              : issue.status === 'ok_minor' ? 'bg-amber-100 text-amber-700 border-amber-200'
                              : 'bg-slate-100 text-slate-600 border-slate-200';
                  const tagLbl = issue.status === 'mismatch' ? 'DISCREPANZA' : issue.status === 'ok_minor' ? 'piccola differenza' : 'non verificato';
                  return (
                    <div key={i} className="bg-white/70 rounded-md px-3 py-2 border text-xs" data-testid={`data-health-issue-${issue.year}-${issue.month}`}>
                      <div className="flex items-center justify-between mb-1">
                        <strong className="text-slate-800">{MONTHS_IT[issue.month]} {issue.year}</strong>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${tagBg}`}>{tagLbl}</span>
                      </div>
                      {issue.status !== 'unknown' && (
                        <div className="space-y-0.5 text-[11px] text-slate-600 tabular-nums">
                          {issue.pdf_revenue != null && (
                            <div className="flex items-center justify-between">
                              <span>Ricavi:</span>
                              <span>
                                <span className="text-slate-700">{issue.analysis_revenue?.toLocaleString('it-IT') ?? '—'} €</span>
                                <span className="text-slate-400"> vs PDF </span>
                                <span className="font-semibold">{issue.pdf_revenue?.toLocaleString('it-IT') ?? '—'} €</span>
                                {Math.abs(issue.revenue_diff || 0) >= 1 && <span className="text-red-600 ml-1">({issue.revenue_diff >= 0 ? '+' : ''}{issue.revenue_diff} €)</span>}
                              </span>
                            </div>
                          )}
                          {issue.pdf_utile != null && (
                            <div className="flex items-center justify-between">
                              <span>Utile:</span>
                              <span>
                                <span className="text-slate-700">{issue.analysis_utile?.toLocaleString('it-IT') ?? '—'} €</span>
                                <span className="text-slate-400"> vs PDF </span>
                                <span className="font-semibold">{issue.pdf_utile?.toLocaleString('it-IT') ?? '—'} €</span>
                                {Math.abs(issue.utile_diff || 0) >= 1 && <span className={`ml-1 ${issue.status === 'mismatch' ? 'text-red-600' : 'text-amber-600'}`}>({issue.utile_diff >= 0 ? '+' : ''}{issue.utile_diff} €)</span>}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                      {issue.status === 'unknown' && (
                        <div className="text-[11px] text-slate-500">
                          Ricavi salvati: {issue.analysis_revenue?.toLocaleString('it-IT') ?? '—'} € — manca il riferimento al bilancio originale per la verifica.
                        </div>
                      )}
                      {issue.injected_depreciation > 0 && (
                        <div className="text-[10px] text-blue-700 mt-1 italic">
                          ℹ️ Ammortamenti iniettati: {issue.injected_depreciation.toFixed(2)} € (esclusi dal confronto col PDF)
                        </div>
                      )}
                      {issue.status === 'mismatch' && (
                        <div className="mt-1.5 pt-1.5 border-t border-red-200 flex flex-wrap gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDiagnose(issue.year, issue.month)}
                            className="h-6 text-[10px] px-2 border-slate-300"
                            data-testid={`data-health-diagnose-${issue.year}-${issue.month}`}
                          >
                            <Search className="h-3 w-3 mr-1" />
                            Spiega differenza
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleAccept(issue.period_id)}
                            disabled={accepting[issue.period_id]}
                            className="h-6 text-[10px] px-2 border-slate-300"
                            data-testid={`data-health-accept-${issue.year}-${issue.month}`}
                          >
                            <ShieldCheck className="h-3 w-3 mr-1" />
                            {accepting[issue.period_id] ? "..." : "Marca come accettato"}
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {sortedIssues.length > 6 && (
                <div className="text-[11px] text-slate-500 mt-2 italic">…e altri {sortedIssues.length - 6} periodi.</div>
              )}

              <div className="text-[11px] text-slate-500 mt-2 flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                {health.ok_count}/{health.total_periods} periodi OK
              </div>
            </>
          )}
        </div>
      </div>

      {/* Dialog Spiega differenza */}
      <Dialog open={diagOpen} onOpenChange={setDiagOpen}>
        <DialogContent className="max-w-2xl" data-testid="data-health-diagnose-dialog">
          <DialogHeader>
            <DialogTitle>
              Analisi della differenza · {diagData ? `${MONTHS_IT[diagData.month]} ${diagData.year}` : ''}
            </DialogTitle>
            <DialogDescription>
              Confronto tra l'utile ricalcolato dall'app e quello stampato sul PDF.
            </DialogDescription>
          </DialogHeader>

          {diagLoading && (
            <div className="text-sm text-muted-foreground py-6 text-center">Analisi in corso…</div>
          )}

          {!diagLoading && diagData && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2 text-center bg-slate-50 rounded-md p-3">
                <div>
                  <div className="text-[10px] uppercase text-slate-500">PDF</div>
                  <div className="font-bold tabular-nums">{diagData.pdf_utile?.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-slate-500">App (corretto)</div>
                  <div className="font-bold tabular-nums">{diagData.analysis_utile_adjusted?.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</div>
                  {diagData.injected_depreciation > 0 && (
                    <div className="text-[9px] text-blue-700 italic">+{diagData.injected_depreciation.toFixed(2)} ammort. iniettati</div>
                  )}
                </div>
                <div>
                  <div className="text-[10px] uppercase text-slate-500">Differenza</div>
                  <div className={`font-bold tabular-nums ${(diagData.utile_diff || 0) >= 0 ? 'text-amber-700' : 'text-red-700'}`}>
                    {diagData.utile_diff >= 0 ? '+' : ''}{diagData.utile_diff?.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                  </div>
                </div>
              </div>

              {diagData.suspects_count === 0 || diagData.suspects.every(s => Math.abs(s.delta_if_fixed) < 1) ? (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-semibold text-emerald-800 mb-1">Nessun mapping da correggere</div>
                      <div className="text-emerald-700 text-xs">
                        Tutte le voci del periodo sono classificate come suggerito dal codice di conto.
                        La differenza di <strong>{Math.abs(diagData.utile_diff || 0).toFixed(2)} €</strong> dipende
                        probabilmente da arrotondamenti interni al software contabile o da una rettifica
                        non ancora riprodotta nelle entries del PDF. È sicuro <strong>marcarla come accettata</strong>.
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="text-xs text-slate-600">
                    Voci sospette ordinate per impatto stimato sull'utile.
                    Se "Δ se sistemato" è ≠ 0, una correzione del mapping <strong>potrebbe</strong> ridurre la differenza.
                  </div>
                  <div className="max-h-80 overflow-y-auto border rounded-md divide-y">
                    {diagData.suspects.map((s, i) => (
                      <div key={i} className="px-3 py-2 text-xs hover:bg-slate-50">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="font-mono text-[10px] text-slate-500">{s.account_code || '—'}</span>
                          <span className={`font-bold tabular-nums ${s.delta_if_fixed > 0 ? 'text-emerald-700' : s.delta_if_fixed < 0 ? 'text-red-700' : 'text-slate-500'}`}>
                            Δ se sistemato: {s.delta_if_fixed >= 0 ? '+' : ''}{s.delta_if_fixed.toFixed(2)} €
                          </span>
                        </div>
                        <div className="text-slate-800 font-medium mb-0.5">{s.account_name || 'Senza nome'}</div>
                        <div className="text-[11px] text-slate-600 mb-1 tabular-nums">
                          Importo: <strong>{s.amount.toLocaleString('it-IT', { minimumFractionDigits: 2 })} €</strong>
                          {' · '}attuale: <span className="font-mono">{s.current_category}{s.current_subcategory ? `/${s.current_subcategory}` : ''}</span>
                          {s.suggested_category && s.suggested_category !== s.current_category && (
                            <> {' · '}suggerito: <span className="font-mono text-emerald-700">{s.suggested_category}{s.suggested_subcategory ? `/${s.suggested_subcategory}` : ''}</span></>
                          )}
                        </div>
                        <ul className="list-disc list-inside text-[11px] text-slate-500 space-y-0.5">
                          {s.reasons.map((r, j) => <li key={j}>{r}</li>)}
                        </ul>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <div className="text-[10px] text-slate-400 italic">{diagData.note}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DataHealthBanner;
